import { useMemo } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { Article } from '@web/newsfeed/types/Article';
import { getArticleSearchQueryKey, getArticleQueryKey } from '@web/newsfeed/utils/getQueryKey';
import { useArticleSearchContext } from '@web/newsfeed/ArticleSearch/ArticleSearchProvider/ArticleSearchProvider';

export const useArticleSearchQueryCache = () => {
  const queryClient = useQueryClient();
  const { searchPageId, searchQuery } = useArticleSearchContext();

  return useMemo(() => {
    const addArticleLike = (articleId: ID) => {
      const searchQueryKey = getArticleSearchQueryKey(searchPageId, searchQuery);
      const detailsQueryKey = getArticleQueryKey(articleId);
      const updateArticle = (article: Article): Article =>
        article.id === articleId
          ? { ...article, likesCount: (article.likesCount || 0) + 1, likedByMe: true }
          : article;

      queryClient.setQueryData<InfiniteData<Article[]>>(searchQueryKey, (cachedData) => {
        if (!cachedData) return;

        return {
          ...cachedData,
          pages: cachedData.pages.map((articles) => articles.map(updateArticle)),
        };
      });

      queryClient.setQueryData<Article>(detailsQueryKey, (cachedData) => {
        if (!cachedData) return;
        return updateArticle(cachedData);
      });
    };

    const removeArticleLike = (articleId: ID) => {
      const searchQueryKey = getArticleSearchQueryKey(searchPageId, searchQuery);
      const detailsQueryKey = getArticleQueryKey(articleId);

      const updateArticle = (article: Article): Article =>
        article.id === articleId
          ? {
              ...article,
              likesCount: article.likesCount > 0 ? article.likesCount - 1 : 0,
              likedByMe: false,
            }
          : article;

      queryClient.setQueryData<InfiniteData<Article[]>>(searchQueryKey, (cachedData) => {
        if (!cachedData) return;

        return {
          ...cachedData,
          pages: cachedData.pages.map((articles) => articles.map(updateArticle)),
        };
      });

      queryClient.setQueryData<Article>(detailsQueryKey, (cachedData) => {
        if (!cachedData) return;
        return updateArticle(cachedData);
      });
    };

    return {
      addArticleLike,
      removeArticleLike,
    };
  }, [queryClient, searchPageId, searchQuery]);
};
